import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import tradesService from '../services/tradesService';

const JOB_POLL_INTERVAL_MS = 3000;
const TERMINAL_JOB_STATUSES = ['completed', 'failed'];

const formatMoney = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatNumber = (value, maxFraction = 4) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFraction,
  });
};

const formatDateTime = (isoValue) => {
  if (!isoValue) return 'N/A';
  const dt = new Date(isoValue);
  return Number.isNaN(dt.getTime()) ? isoValue : dt.toLocaleString();
};

const quarterToDateRange = (quarterValue) => {
  const input = String(quarterValue || '').trim().toUpperCase();
  const match = input.match(/^(?:([0-9]{4})\s*[-_]?\s*Q([1-4])|Q([1-4])\s*[-_]?\s*([0-9]{4}))$/);
  if (!match) return null;

  const year = Number(match[1] || match[4]);
  const quarter = Number(match[2] || match[3]);
  const startMonth = (quarter - 1) * 3;
  const start = new Date(Date.UTC(year, startMonth, 1));
  const end = new Date(Date.UTC(year, startMonth + 3, 0));

  const toIsoDate = (dt) => dt.toISOString().slice(0, 10);
  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(end),
  };
};

const errorText = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

const jobBadgeClass = (jobStatus) => {
  if (jobStatus === 'completed') return 'bg-success';
  if (jobStatus === 'failed') return 'bg-danger';
  if (jobStatus === 'processing') return 'bg-primary';
  return 'bg-secondary';
};

const jobProgressPercent = (job) => {
  if (job.status === 'completed') return 100;
  const target = Number(job.total_trades || 0);
  if (!target) return job.status === 'queued' ? 5 : 15;
  return Math.min(99, Math.round((Number(job.written_count || 0) / target) * 100));
};

/**
 * Admin-only panel for uploading the firm's quarterly custodian trade reports
 * (one Schwab export and one Fidelity export per quarter) and reviewing the
 * consolidated quarter data built from them.
 */
export default function CompanyTradeReportsPanel() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [reportFile, setReportFile] = useState(null);
  const [quarterInput, setQuarterInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [job, setJob] = useState(null);

  const [reports, setReports] = useState([]);
  const [reportsError, setReportsError] = useState('');
  const [deletingReportId, setDeletingReportId] = useState('');
  const [downloadingReportId, setDownloadingReportId] = useState('');

  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [rangeTrades, setRangeTrades] = useState([]);
  const [rangeTradesLoading, setRangeTradesLoading] = useState(false);
  const [rangeTradesError, setRangeTradesError] = useState('');
  const [rangeQuartersLoaded, setRangeQuartersLoaded] = useState([]);
  const [rangeSort, setRangeSort] = useState({ key: 'trade_date', direction: 'desc' });
  const [rangeFilters, setRangeFilters] = useState({
    search: '',
    action: 'all',
    source: 'all',
    brokerage: 'all',
    symbol: 'all',
  });

  const pollTimerRef = useRef(null);

  const loadReports = useCallback(async () => {
    setReportsError('');
    try {
      const result = await tradesService.getCompanyReports(100);
      setReports(result.reports || []);
    } catch (err) {
      console.error('Failed to load company reports:', err);
      setReportsError(errorText(err, 'Failed to load uploaded company reports.'));
    }
  }, []);

  const resetImportModal = useCallback(() => {
    setReportFile(null);
    setQuarterInput('');
    setPreview(null);
    setBusy(false);
    setError('');
    setStatus('');
    setJob(null);
  }, []);

  const openImportModal = useCallback(() => {
    resetImportModal();
    setShowImportModal(true);
  }, [resetImportModal]);

  const closeImportModal = useCallback(() => {
    resetImportModal();
    setShowImportModal(false);
  }, [resetImportModal]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const startPolling = useCallback((jobId) => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const result = await tradesService.getCompanyImportJob(jobId);
        const nextJob = result?.job;
        if (!nextJob?.job_id) {
          throw new Error('Import job status response did not include a job id.');
        }
        setJob(nextJob);
        if (TERMINAL_JOB_STATUSES.includes(nextJob.status)) {
          stopPolling();
          setBusy(false);
          if (nextJob.status === 'completed') {
            setStatus(nextJob.progress_message || 'Import complete.');
            const nextQuarter = nextJob.quarters_covered?.[0] || nextJob.report_quarter || '';
            setSelectedQuarter(nextQuarter);
            const range = quarterToDateRange(nextQuarter);
            if (range) {
              setCustomStartDate(range.startDate);
              setCustomEndDate(range.endDate);
            }
            await loadReports();
          } else {
            setError(nextJob.error || 'The import job failed.');
            setStatus('');
          }
        }
      } catch (err) {
        console.error('Failed to poll company import job:', err);
        stopPolling();
        setBusy(false);
        setError(errorText(err, 'Lost track of the import job.'));
      }
    }, JOB_POLL_INTERVAL_MS);
  }, [loadReports, stopPolling]);

  const availableQuarters = useMemo(() => {
    const unique = new Set();
    reports.forEach((report) => {
      (report.quarters_covered || []).forEach((quarter) => unique.add(quarter));
      if (report.quarter) unique.add(report.quarter);
    });
    return Array.from(unique).sort().reverse();
  }, [reports]);

  useEffect(() => {
    if (!selectedQuarter && availableQuarters.length > 0) {
      const defaultQuarter = availableQuarters[0];
      setSelectedQuarter(defaultQuarter);
      const range = quarterToDateRange(defaultQuarter);
      if (range) {
        setCustomStartDate(range.startDate);
        setCustomEndDate(range.endDate);
      }
    }
  }, [availableQuarters, selectedQuarter]);

  const resetRangeResults = useCallback(() => {
    setRangeTrades([]);
    setRangeQuartersLoaded([]);
    setRangeTradesError('');
  }, []);

  const toggleRangeSort = useCallback((key) => {
    setRangeSort((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        key,
        direction: key === 'trade_date' ? 'desc' : 'asc',
      };
    });
  }, []);

  const sortedRangeTrades = useMemo(() => {
    const getComparable = (row, key) => {
      if (key === 'trade_date') return String(row.trade_date || '');
      if (key === 'trade_time') return String(row.trade_time || '');
      if (key === 'broker') return String(row.broker || '').toUpperCase();
      if (key === 'symbol') return String(row.symbol || '').toUpperCase();
      if (key === 'action') return String(row.action || '').toUpperCase();
      if (key === 'quantity') return Number(row.quantity || 0);
      if (key === 'price') return Number(row.price || 0);
      if (key === 'notional') return Number(row.notional || 0);
      if (key === 'account') {
        return `${String(row.account_number || '')} ${String(row.account_name || '')}`.toUpperCase();
      }
      return '';
    };

    const sorted = [...rangeTrades];
    const directionFactor = rangeSort.direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const left = getComparable(a, rangeSort.key);
      const right = getComparable(b, rangeSort.key);

      if (typeof left === 'number' && typeof right === 'number') {
        const delta = left - right;
        if (delta !== 0) return delta * directionFactor;
      } else {
        const delta = String(left).localeCompare(String(right));
        if (delta !== 0) return delta * directionFactor;
      }

      return String(a.trade_id || '').localeCompare(String(b.trade_id || ''));
    });

    return sorted;
  }, [rangeSort.direction, rangeSort.key, rangeTrades]);

  const sortIconClass = useCallback((key) => {
    if (rangeSort.key !== key) return 'bi-arrow-down-up text-muted';
    return rangeSort.direction === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }, [rangeSort.direction, rangeSort.key]);

  const uniqueRangeBrokerages = useMemo(() => {
    return Array.from(new Set((rangeTrades || []).map((trade) => trade.broker).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [rangeTrades]);

  const uniqueRangeSymbols = useMemo(() => {
    return Array.from(new Set((rangeTrades || []).map((trade) => trade.symbol).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [rangeTrades]);

  const filteredAndSortedRangeTrades = useMemo(() => {
    const searchTerm = rangeFilters.search.trim().toLowerCase();

    return sortedRangeTrades.filter((trade) => {
      const matchesSearch = !searchTerm || [
        trade.symbol,
        trade.broker,
        trade.account_name,
        trade.account_number,
        trade.action,
      ].some((value) => String(value ?? '').toLowerCase().includes(searchTerm));

      const matchesAction = rangeFilters.action === 'all' || String(trade.action || '').toUpperCase() === rangeFilters.action;
      const matchesSource = rangeFilters.source === 'all'
        || (rangeFilters.source === 'manual' && !(trade.hybridized || trade.auto_processed))
        || (rangeFilters.source === 'auto' && Boolean(trade.auto_processed) && !trade.hybridized)
        || (rangeFilters.source === 'hybrid' && Boolean(trade.hybridized));
      const matchesBrokerage = rangeFilters.brokerage === 'all' || String(trade.broker || '') === rangeFilters.brokerage;
      const matchesSymbol = rangeFilters.symbol === 'all' || String(trade.symbol || '') === rangeFilters.symbol;

      return matchesSearch && matchesAction && matchesSource && matchesBrokerage && matchesSymbol;
    });
  }, [rangeFilters, sortedRangeTrades]);

  const handleFileChange = (event) => {
    setReportFile(event.target.files?.[0] || null);
    setPreview(null);
    setJob(null);
    setError('');
    setStatus('');
  };

  const handlePreview = async () => {
    if (!reportFile) return;
    setBusy(true);
    setError('');
    setJob(null);
    setStatus('Parsing report and consolidating trades...');
    try {
      const result = await tradesService.previewCompanyReport({
        file: reportFile,
        quarter: quarterInput.trim(),
      });
      setPreview(result);
      const coveredQuarters = Array.isArray(result.quarters_covered)
        ? result.quarters_covered
        : [];
      setStatus(
        `Detected ${result.broker} report covering ${coveredQuarters.join(', ') || result.quarter || 'unknown quarter'}: `
        + `${result.new_trade_count} new trades, `
        + `${result.duplicate_trade_count} already stored from earlier uploads.`,
      );
    } catch (err) {
      console.error('Failed to preview company report:', err);
      setPreview(null);
      setError(errorText(err, 'Failed to parse the uploaded company report.'));
      setStatus('');
    } finally {
      setBusy(false);
    }
  };

  const handleStore = async () => {
    if (!reportFile || !preview) return;
    setBusy(true);
    setError('');
    setStatus('Uploading report and queueing the import...');
    try {
      const result = await tradesService.startCompanyImportJob({
        file: reportFile,
        quarter: preview.quarter,
      });
      const queuedJob = result?.job;
      const queuedJobId = queuedJob?.job_id || result?.job_id;
      if (!queuedJob || !queuedJobId) {
        throw new Error('Import was queued response without a job id.');
      }

      setJob(queuedJob);
      setStatus('Import queued. This runs in the background and can take a few minutes.');
      startPolling(queuedJobId);
    } catch (err) {
      console.error('Failed to queue company import:', err);
      setError(errorText(err, 'Failed to queue the company report import.'));
      setStatus('');
      setBusy(false);
    }
  };

  const handleQuarterSelection = useCallback((value) => {
    setSelectedQuarter(value);
    const range = quarterToDateRange(value);
    if (range) {
      setCustomStartDate(range.startDate);
      setCustomEndDate(range.endDate);
    }
    resetRangeResults();
  }, [resetRangeResults]);

  const loadRangeTrades = useCallback(async () => {
    setRangeTradesError('');

    if (!customStartDate || !customEndDate) {
      setRangeTrades([]);
      setRangeQuartersLoaded([]);
      setRangeTradesError('Pick both a start and end date.');
      return;
    }
    if (customEndDate < customStartDate) {
      setRangeTrades([]);
      setRangeQuartersLoaded([]);
      setRangeTradesError('End date must be on or after start date.');
      return;
    }

    const overlappingQuarters = availableQuarters.filter((quarter) => {
      const range = quarterToDateRange(quarter);
      if (!range) return false;
      return !(range.endDate < customStartDate || range.startDate > customEndDate);
    });

    if (overlappingQuarters.length === 0) {
      setRangeTrades([]);
      setRangeQuartersLoaded([]);
      return;
    }

    setRangeTradesLoading(true);
    try {
      const quarterResults = await Promise.all(
        overlappingQuarters.map((quarter) => tradesService.getCompanyTrades({ quarter, limit: 5000 })),
      );

      const uniqueById = new Map();
      quarterResults.forEach((result) => {
        (result?.trades || []).forEach((trade) => {
          const tradeDate = String(trade.trade_date || '');
          if (tradeDate >= customStartDate && tradeDate <= customEndDate) {
            uniqueById.set(trade.trade_id, trade);
          }
        });
      });

      const rows = Array.from(uniqueById.values()).sort((a, b) => {
        const dateCmp = String(b.trade_date || '').localeCompare(String(a.trade_date || ''));
        if (dateCmp !== 0) return dateCmp;
        return String(a.symbol || '').localeCompare(String(b.symbol || ''));
      });

      setRangeTrades(rows);
      setRangeQuartersLoaded(overlappingQuarters);
    } catch (err) {
      console.error('Failed to load company trades for date range:', err);
      setRangeTrades([]);
      setRangeQuartersLoaded([]);
      setRangeTradesError(errorText(err, 'Failed to load trades for this date range.'));
    } finally {
      setRangeTradesLoading(false);
    }
  }, [availableQuarters, customEndDate, customStartDate]);

  const handleDeleteReport = useCallback(async (report) => {
    if (!report || Number(report.new_trade_count || 0) > 0) {
      return;
    }

    const confirmation = window.confirm(
      `Delete report \"${report.filename}\"?\n\n`
      + 'Only report metadata with zero new rows can be deleted. '
      + 'This will remove it from Uploaded Reports.',
    );
    if (!confirmation) return;

    setError('');
    setStatus('Deleting duplicate report...');
    setDeletingReportId(report.report_id);
    try {
      await tradesService.deleteCompanyReport(report.report_id);
      setStatus('Duplicate report deleted.');
      await loadReports();
    } catch (err) {
      console.error('Failed to delete company report:', err);
      setError(errorText(err, 'Failed to delete company report.'));
      setStatus('');
    } finally {
      setDeletingReportId('');
    }
  }, [loadReports]);

  const handleDownloadReport = useCallback(async (report) => {
    if (!report?.report_id) return;

    setError('');
    setStatus('Preparing report download...');
    setDownloadingReportId(report.report_id);
    try {
      const result = await tradesService.downloadCompanyReport(report.report_id);
      const objectUrl = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = result.filename || report.filename || 'company-report.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      setStatus(`Downloaded ${link.download}.`);
    } catch (err) {
      console.error('Failed to download company report:', err);
      setError(errorText(err, 'Failed to download company report.'));
      setStatus('');
    } finally {
      setDownloadingReportId('');
    }
  }, []);

  const previewSummary = preview?.summary;

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-header bg-white d-flex align-items-center justify-content-between gap-2">
        <button
          type="button"
          className="btn btn-sm btn-link text-decoration-none text-body p-0 d-inline-flex align-items-center"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-controls="company-trade-reports-body"
        >
          <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`} aria-hidden="true"></i>
          <span className="fw-semibold">
            <i className="bi bi-building me-2"></i>
            Company Trade Reports
          </span>
        </button>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={openImportModal}
            disabled={busy}
          >
            <i className="bi bi-upload me-1"></i>
            Import
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={loadReports}
            disabled={busy}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </button>
        </div>
      </div>
      <div
        id="company-trade-reports-body"
        className={isExpanded ? 'card-body' : 'card-body d-none'}
      >

        <h6 className="fw-semibold">Uploaded Reports</h6>
        {reportsError && <div className="alert alert-warning py-2">{reportsError}</div>}
        {reports.length === 0 ? (
          <div className="text-muted small">No company reports have been uploaded yet.</div>
        ) : (
          <>
            <div className="table-responsive" style={{ maxHeight: '260px' }}>
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Quarters</th>
                  <th>Broker</th>
                  <th>File</th>
                  <th className="text-end">New / Duplicate</th>
                  <th className="text-end">Buy / Sell Notional</th>
                  <th>Uploaded</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.report_id}>
                    <td>{(report.quarters_covered || []).join(', ') || report.quarter}</td>
                    <td>{report.broker}</td>
                    <td className="small text-break">{report.filename}</td>
                    <td className="text-end">
                      {formatNumber(report.new_trade_count, 0)} /
                      {' '}{formatNumber(report.duplicate_trade_count, 0)}
                    </td>
                    <td className="text-end">
                      {formatMoney(report.summary?.total_buy_notional)} /
                      {' '}{formatMoney(report.summary?.total_sell_notional)}
                    </td>
                    <td className="small">{formatDateTime(report.uploaded_at)}</td>
                    <td className="text-end">
                      <div className="d-inline-flex gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={busy || downloadingReportId === report.report_id}
                          onClick={() => handleDownloadReport(report)}
                          title="Download archived report workbook"
                        >
                          {downloadingReportId === report.report_id ? 'Downloading...' : 'Download'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={
                            busy
                            || deletingReportId === report.report_id
                            || Number(report.new_trade_count || 0) > 0
                          }
                          onClick={() => handleDeleteReport(report)}
                          title={
                            Number(report.new_trade_count || 0) > 0
                              ? 'Only reports with 0 new rows can be deleted.'
                              : 'Delete this duplicate report metadata'
                          }
                        >
                          {deletingReportId === report.report_id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}

        <hr />

        <div className="d-flex flex-wrap align-items-end gap-2">
          <div className="me-3">
            <label className="form-label fw-semibold mb-1" htmlFor="company-summary-quarter">
              Quarter Preset
            </label>
            <select
              id="company-summary-quarter"
              className="form-select"
              value={selectedQuarter}
              onChange={(e) => handleQuarterSelection(e.target.value)}
              disabled={availableQuarters.length === 0}
            >
              {availableQuarters.length === 0 && <option value="">No quarters stored</option>}
              {availableQuarters.map((quarter) => (
                <option key={quarter} value={quarter}>{quarter}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label fw-semibold mb-1" htmlFor="company-summary-start-date">
              Start Date
            </label>
            <input
              id="company-summary-start-date"
              type="date"
              className="form-control"
              value={customStartDate}
              onChange={(e) => {
                setCustomStartDate(e.target.value);
                resetRangeResults();
              }}
            />
          </div>
          <div>
            <label className="form-label fw-semibold mb-1" htmlFor="company-summary-end-date">
              End Date
            </label>
            <input
              id="company-summary-end-date"
              type="date"
              className="form-control"
              value={customEndDate}
              onChange={(e) => {
                setCustomEndDate(e.target.value);
                resetRangeResults();
              }}
            />
          </div>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={loadRangeTrades}
            disabled={rangeTradesLoading || !customStartDate || !customEndDate}
          >
            {rangeTradesLoading ? 'Loading...' : 'Load Trades for Range'}
          </button>
        </div>
        <div className="small text-muted mt-2">
          Selecting a quarter auto-fills the custom date range. You can edit dates manually for any custom range.
        </div>

        {rangeTradesError && <div className="alert alert-danger mt-2 py-2">{rangeTradesError}</div>}

        {!rangeTradesError && customStartDate && customEndDate && (
          <div className="small text-muted mt-2">
            {rangeTrades.length > 0
              ? `${formatNumber(rangeTrades.length, 0)} trades from ${customStartDate} to ${customEndDate} across ${formatNumber(rangeQuartersLoaded.length, 0)} quarter(s).`
              : `No trades loaded yet for ${customStartDate} to ${customEndDate}.`}
          </div>
        )}

        {rangeTrades.length > 0 && (
          <>
            <div className="mt-3">
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label small mb-1">Search</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={rangeFilters.search}
                    onChange={(e) => setRangeFilters((prev) => ({ ...prev, search: e.target.value }))}
                    placeholder="Symbol, account, broker..."
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Action</label>
                  <select
                    className="form-select form-select-sm"
                    value={rangeFilters.action}
                    onChange={(e) => setRangeFilters((prev) => ({ ...prev, action: e.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="BUY">Buy</option>
                    <option value="SELL">Sell</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Source</label>
                  <select
                    className="form-select form-select-sm"
                    value={rangeFilters.source}
                    onChange={(e) => setRangeFilters((prev) => ({ ...prev, source: e.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="manual">Manual</option>
                    <option value="auto">Auto</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Brokerage</label>
                  <select
                    className="form-select form-select-sm"
                    value={rangeFilters.brokerage}
                    onChange={(e) => setRangeFilters((prev) => ({ ...prev, brokerage: e.target.value }))}
                  >
                    <option value="all">All</option>
                    {uniqueRangeBrokerages.map((brokerage) => (
                      <option key={brokerage} value={brokerage}>{brokerage}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Symbol</label>
                  <select
                    className="form-select form-select-sm"
                    value={rangeFilters.symbol}
                    onChange={(e) => setRangeFilters((prev) => ({ ...prev, symbol: e.target.value }))}
                  >
                    <option value="all">All</option>
                    {uniqueRangeSymbols.map((symbol) => (
                      <option key={symbol} value={symbol}>{symbol}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-1 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary form-control form-control-sm"
                    onClick={() => setRangeFilters({ search: '', action: 'all', source: 'all', brokerage: 'all', symbol: 'all' })}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <div className="table-responsive mt-2" style={{ maxHeight: '380px' }}>
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('trade_date')}>
                        Trade Date <i className={`bi ${sortIconClass('trade_date')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('trade_time')}>
                        Time <i className={`bi ${sortIconClass('trade_time')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('broker')}>
                        Broker <i className={`bi ${sortIconClass('broker')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('symbol')}>
                        Symbol <i className={`bi ${sortIconClass('symbol')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('action')}>
                        Action <i className={`bi ${sortIconClass('action')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th className="text-end">
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('quantity')}>
                        Quantity <i className={`bi ${sortIconClass('quantity')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th className="text-end">
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('price')}>
                        Price <i className={`bi ${sortIconClass('price')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th className="text-end">
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('notional')}>
                        Notional <i className={`bi ${sortIconClass('notional')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => toggleRangeSort('account')}>
                        Account <i className={`bi ${sortIconClass('account')} ms-1`} aria-hidden="true"></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedRangeTrades.map((row) => (
                    <tr key={row.trade_id}>
                      <td>{row.trade_date}</td>
                      <td>{row.trade_time || '—'}</td>
                      <td>{row.broker}</td>
                      <td className="fw-semibold">{row.symbol}</td>
                      <td>
                        <span className={`badge ${row.action === 'BUY' ? 'bg-success' : 'bg-danger'}`}>
                          {row.action}
                        </span>
                      </td>
                      <td className="text-end">{formatNumber(row.quantity)}</td>
                      <td className="text-end">{formatNumber(row.price, 4)}</td>
                      <td className="text-end">{formatMoney(row.notional)}</td>
                      <td className="small">
                        {row.account_number}
                        {row.account_name ? ` · ${row.account_name}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showImportModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeImportModal}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) closeImportModal(); }}
          >
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Company Trade Report Import</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeImportModal}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    Upload a Schwab order-status export or a Fidelity trade-detail export for any period.
                    The raw workbook is archived in S3 and the trades are consolidated before being appended.
                  </p>

                  {error && <div className="alert alert-danger py-2">{error}</div>}
                  {status && <div className="alert alert-info py-2">{status}</div>}

                  {!preview && (
                    <>
                      <div className="mb-3">
                        <label className="form-label fw-semibold" htmlFor="company-report-file-modal">
                          Report File (.xlsx)
                        </label>
                        <input
                          id="company-report-file-modal"
                          type="file"
                          className="form-control"
                          accept=".xlsx,.xlsm"
                          onChange={handleFileChange}
                          disabled={busy}
                        />
                      </div>

                      <div className="row g-2 align-items-end">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold" htmlFor="company-report-quarter-modal">
                            Label Quarter
                          </label>
                          <input
                            id="company-report-quarter-modal"
                            type="text"
                            className="form-control"
                            placeholder="Auto"
                            value={quarterInput}
                            onChange={(e) => setQuarterInput(e.target.value)}
                            disabled={busy}
                          />
                        </div>
                        <div className="col-md-6 d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary flex-fill"
                            onClick={handlePreview}
                            disabled={busy || !reportFile}
                          >
                            Preview
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {job && (
                    <div className="border rounded p-2 mt-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="fw-semibold">
                          Import job
                          <span className={`badge ms-2 ${jobBadgeClass(job.status)}`}>{job.status}</span>
                        </span>
                        <span className="small text-muted">
                          {formatNumber(job.written_count, 0)} of{' '}
                          {formatNumber(job.total_trades || job.new_trade_count, 0)} written
                        </span>
                      </div>
                      <div className="progress mt-2" style={{ height: '6px' }}>
                        <div
                          className={`progress-bar ${job.status === 'failed' ? 'bg-danger' : ''}`}
                          role="progressbar"
                          style={{ width: `${jobProgressPercent(job)}%` }}
                          aria-valuenow={jobProgressPercent(job)}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                      {job.progress_message && (
                        <div className="small text-muted mt-1">{job.progress_message}</div>
                      )}
                      {job.error && <div className="small text-danger mt-1">{job.error}</div>}
                    </div>
                  )}

                  {preview && previewSummary && (
                    <div className="mt-3">
                      <div className="row g-2 flex-nowrap overflow-auto pb-1">
                        <div className="col" style={{ minWidth: 0 }}>
                          <div className="border rounded p-2 h-100">
                            <div className="text-muted small">Broker / Quarters</div>
                            <div className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                              {preview.broker} · {(preview.quarters_covered || []).join(', ')}
                            </div>
                          </div>
                        </div>
                        <div className="col" style={{ minWidth: 0 }}>
                          <div className="border rounded p-2 h-100">
                            <div className="text-muted small">New / Already Stored</div>
                            <div className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                              {formatNumber(preview.new_trade_count, 0)} new
                              <span className="text-muted small ms-1">
                                ({formatNumber(preview.duplicate_trade_count, 0)} duplicate)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col" style={{ minWidth: 0 }}>
                          <div className="border rounded p-2 h-100">
                            <div className="text-muted small">Consolidated Trades</div>
                            <div className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                              {formatNumber(previewSummary.total_trades, 0)}
                              <span className="text-muted small ms-1">
                                ({formatNumber(previewSummary.buy_trades, 0)} buy /
                                {' '}{formatNumber(previewSummary.sell_trades, 0)} sell)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col" style={{ minWidth: 0 }}>
                          <div className="border rounded p-2 h-100">
                            <div className="text-muted small">Buy / Sell Notional</div>
                            <div className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                              {formatMoney(previewSummary.total_buy_notional)} /
                              {' '}{formatMoney(previewSummary.total_sell_notional)}
                            </div>
                          </div>
                        </div>
                        <div className="col" style={{ minWidth: 0 }}>
                          <div className="border rounded p-2 h-100">
                            <div className="text-muted small">Accounts / Symbols</div>
                            <div className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                              {formatNumber(previewSummary.account_count, 0)} /
                              {' '}{formatNumber(previewSummary.symbol_count, 0)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {preview.skipped_row_count > 0 && (
                        <div className="small text-muted mt-2">
                          Skipped {preview.skipped_row_count} rows that were cancelled or unusable.
                        </div>
                      )}
                      {(preview.warnings || []).map((warning) => (
                        <div key={warning} className="small text-warning mt-1">{warning}</div>
                      ))}

                      <div className="table-responsive mt-2" style={{ maxHeight: '320px' }}>
                        <table className="table table-sm table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Trade Date</th>
                              <th>Symbol</th>
                              <th>Action</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Price</th>
                              <th className="text-end">Notional</th>
                              <th>Account</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(preview.sample_rows || []).map((row) => (
                              <tr key={row.trade_id}>
                                <td>{row.trade_date}</td>
                                <td className="fw-semibold">{row.symbol}</td>
                                <td>
                                  <span className={`badge ${row.action === 'BUY' ? 'bg-success' : 'bg-danger'}`}>
                                    {row.action}
                                  </span>
                                </td>
                                <td className="text-end">{formatNumber(row.quantity)}</td>
                                <td className="text-end">{formatNumber(row.price, 4)}</td>
                                <td className="text-end">{formatMoney(row.notional)}</td>
                                <td className="small">
                                  {row.account_number}
                                  {row.account_name ? ` · ${row.account_name}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="small text-muted mt-1">
                        Showing the first {(preview.sample_rows || []).length} consolidated trades.
                      </div>
                    </div>
                  )}

                  {preview && (
                    <div className="mt-4 d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={closeImportModal}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={job?.status === 'completed' ? closeImportModal : handleStore}
                        disabled={busy && job?.status !== 'completed' || (!job && (!preview || preview.new_trade_count === 0))}
                      >
                        {job?.status === 'completed' ? 'Done' : (busy ? 'Importing...' : 'Import')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
